import React, { useEffect, useMemo, useState } from 'react';

import { usePatchPeriodicityMutation } from 'src/redux/services/centralApi';
import type { Periodicity } from 'src/interfaces/entities/periodicity';
import BRAZIL_LOCALE from 'src/pages/cron/AddCronPage/i18n';
import { Button, Form, Input, Modal, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { isValidCron } from 'cron-validator';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Cron from 'react-js-cron';

type IEditCronForm = Pick<Periodicity<'persisted'>, 'alias'>;

export default function EditCron(
	{
		row,
	}: { row: Periodicity<'persisted'> }
) {
	const [ open, setOpen, ] = useState<boolean>(false);
	const [ cron, setCron, ] = useState<string>(row.cron_expression);
	const [ patchPeriodicity, patchPeriodicityResult, ] = usePatchPeriodicityMutation();

	const [ editPeriodicityForm, ] = Form.useForm<IEditCronForm>();

	const disabled = useMemo(() => {
		return row.org_id === null;
	}, [ row, ]);

	const isCronIntervalLowerOrEqualThan30Minutes = () => {
		const [ minute, hour, day, month, week, ] = cron.split(/\s*/g);
		if(hour === '*' && day === '*' && month === '*' && week === '*'){
			return minute === '*';
		}
		return false;
	};

	const onFinishEditCron = (data: IEditCronForm) => {
		if(!isValidCron(cron)){
			toast.info('A expressão cron está inválida ou incompleta!');
			return;
		}

		if(isCronIntervalLowerOrEqualThan30Minutes()){
			toast.error('O intervalo cron deve ser maior que 30 minutos!');
			return;
		}

		patchPeriodicity({
			id: row.periodicity_id,
			periodicity: {
				alias: data.alias,
				cron_expression: cron,
			},
		})
			.unwrap()
			.then(() => {
				toast.success('Cron editado com sucesso!');
			})
			.catch(() => {
				toast.error('Erro ao editar cron!');
			})
			.finally(() => {
				setOpen(false);
			});
	};

	useEffect(() => {
		resetFields();
	}, [ open, row, ]);

	const resetFields = () => {
		setCron(row.cron_expression);
	};

	return <>
		<Tooltip
			title={
				disabled && 'Periodicidades padrões não podems ser editadas.'
			}
		>
			<Button
				type="text"
				disabled={disabled}
				icon={<EditOutlined />}
				onClick={() => setOpen(true)}
			/>
		</Tooltip>
		<Modal
			centered
			title={'Editar Cron'}
			open={open}
			onCancel={() => {
				setOpen(false);
				toast.info('Operação cancelada!', { autoClose: 1000, });
			}}
			destroyOnHidden
			confirmLoading={patchPeriodicityResult.isLoading}
			okText="Salvar"
			onOk={() => {
				editPeriodicityForm.submit();
			}}
		>
			<Form
				style={{ marginTop: 20, }}
				onFinish={onFinishEditCron}
				layout="vertical"
				form={editPeriodicityForm}
			>
				<Form.Item
					required
					tooltip={'Define o nome do cron que será exibido no sistema.'}
					hasFeedback
					label="Apelido"
					name="alias"
					initialValue={row.alias}
					rules={[
						{
							min: 2,
							message:
                                'O apelido deve ter no mínimo 2 caracteres!',
						},
						{
							max: 50,
							message:
                                'O apelido deve ter no máximo 50 caracteres!',
						},
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
				>
					<Input
						defaultValue={row?.alias}
						placeholder="Digite o apelido"
					/>
				</Form.Item>
				<Form.Item
					initialValue={cron}
					required
					tooltip={
						<>
                            Define a expressão cron de acordo com o
                            formato{' '}
							<Link
								target="_blank"
								to={'https://crontab.cronhub.io/'}
							>
                                unix
							</Link>
                            .
						</>
					}
					hasFeedback
					label="Expressão Cron"
					name="cron"
				>
					<Cron
						allowedPeriods={[
							'day',
							'hour',
							'month',
							'week',
							'year',
						]}
						dropdownsConfig={{
							minutes: {
								allowClear: false,
								filterOption: ({ value, }) => (
									(value === '0' || value === '30') ? true : false
								),
								mode: 'single',
							},
						}}
						locale={BRAZIL_LOCALE}
						value={cron}
						setValue={setCron}
					/>
				</Form.Item>
			</Form>
		</Modal>
	</>;
}